/**
 * Electron Detection and Utilities
 * 
 * This module provides utilities to detect if the app is running in Electron
 * and provides platform-specific functionality.
 */

// Type declarations for Electron APIs
declare global {
  interface Window {
    electron?: {
      platform: string;
      versions: {
        node: string;
        chrome: string;
        electron: string;
      };
    };
  }
}

/**
 * Check if the app is running in Electron
 */
export const isElectron = (): boolean => {
  // Check for Electron-specific properties
  if (typeof window !== 'undefined') {
    // Method 1: Check for Electron user agent
    if (navigator.userAgent.toLowerCase().indexOf('electron') !== -1) {
      return true;
    }
    
    // Method 2: Check for Electron window object
    if (window.electron) {
      return true;
    }
    
    // Method 3: Check for Electron-specific process
    if (typeof (window as any).process !== 'undefined' && 
        (window as any).process.type === 'renderer') {
      return true;
    }
  }
  
  return false;
};

/**
 * Get the platform information
 */
export const getPlatform = (): 'electron' | 'web' => {
  return isElectron() ? 'electron' : 'web';
};

/**
 * Get Electron version if available
 */
export const getElectronVersion = (): string | null => {
  if (isElectron() && window.electron?.versions?.electron) {
    return window.electron.versions.electron;
  }
  return null;
};

/**
 * Check if running in development mode
 */
export const isDevelopment = (): boolean => {
  return process.env.NODE_ENV === 'development' || 
         process.env.REACT_APP_ENVIRONMENT === 'dev';
};

/**
 * Get the appropriate API base URL based on platform
 */
export const getApiBaseUrl = (): string => {
  const env = process.env.REACT_APP_ENVIRONMENT || 'demo';
  
  if (isElectron()) {
    // In Electron, you might want to use a different URL
    // For now, use the same environment-based URLs
    const envUrls: Record<string, string> = {
      dev: 'http://localhost/tailorstitchpro/backend/api',
      demo: 'https://lahari.site/tailorDemo/backend/api',
      prod: 'https://lahari.site/TailorStitchPro/backend/api',
    };
    return envUrls[env] || envUrls.demo;
  }
  
  // For web, use the same environment-based URLs
  const envUrls: Record<string, string> = {
    dev: 'http://localhost/tailorstitchpro/backend/api',
    demo: 'https://lahari.site/tailorDemo/backend/api',
    prod: 'https://lahari.site/TailorStitchPro/backend/api',
  };
  return envUrls[env] || envUrls.demo;
};

